// La hoja de vista previa (04-11-2026, como Vista Rápida de Apple): UN visor para todo lo que se abre de un chat —PDF,
// imágenes, informes, texto—, desde el chat y desde «Fotos y archivos». Sustituye a VisorImagen + VisorDocumento.
//
// Es una pila: una imagen pulsada DENTRO de un informe abierto se abre encima, y Esc (o la ✕) la quita y deja el informe
// donde estaba. `elementos` + `indice`: las imágenes de un mismo mensaje se recorren con ←/→ (un PDF usa ←/→ para sus
// páginas, por eso se abre solo).
import { create } from 'zustand';
import { claseDe, detalleDe, tituloDe, type Clase, type DatosArchivo } from './presentacion';
import { urlArchivo, urlPortada } from './url';

/** Quién mandó lo que se abre: su nombre y, si se sabe, su icono (el mismo de la lista y del chat). */
export interface Autor {
  nombre: string;
  icono?: string | null;
  semilla?: string;
}

export interface ElementoVista {
  /** Clave estable (React, carril). */
  clave: string;
  clase: Clase;
  titulo: string;
  /** «PDF · 11 páginas · 2,2 MB». */
  detalle: string;
  /** Nombre con el que se descarga. */
  nombre: string;
  /** Los bytes (raw). */
  url: string;
  /** De qué agente es (las imágenes «adjunto:<id>» de un informe se buscan en sus adjuntos). */
  agentId?: string;
  /** PDF: la portada que dibuja el servidor (se enseña mientras pdf.js carga). */
  portada?: string;
  /** PDF: ancho/alto de la página 1. */
  proporcion?: number;
  paginas?: number;
}

export interface Apertura {
  elementos: ElementoVista[];
  indice: number;
  /** Quién lo mandó y cuándo, para la barra («Bruno · hoy, 16:32»). */
  autor?: Autor;
  en?: string;
}

interface EstadoVistaPrevia {
  pila: Apertura[];
  abrir: (a: Apertura) => void;
  /** Quita la de arriba (Esc, ✕, pulsar fuera). */
  cerrar: () => void;
  /** Cambia el elemento visible de la de arriba (←/→ entre imágenes, el carril). */
  ir: (indice: number) => void;
}

export const useVistaPrevia = create<EstadoVistaPrevia>((set) => ({
  pila: [],
  abrir: (a) => set((s) => ({ pila: [...s.pila, { ...a, indice: Math.min(Math.max(0, a.indice), Math.max(0, a.elementos.length - 1)) }] })),
  cerrar: () => set((s) => ({ pila: s.pila.slice(0, -1) })),
  ir: (indice) =>
    set((s) => {
      const arriba = s.pila[s.pila.length - 1];
      if (!arriba || indice < 0 || indice >= arriba.elementos.length || indice === arriba.indice) return s;
      return { pila: [...s.pila.slice(0, -1), { ...arriba, indice }] };
    }),
}));

/** Un archivo guardado de un agente, listo para la hoja. */
export function elementoDe(agentId: string, d: DatosArchivo): ElementoVista {
  const clase = claseDe(d);
  return {
    clave: d.id,
    clase,
    titulo: tituloDe(d),
    detalle: detalleDe(d),
    nombre: d.nombre,
    url: urlArchivo(agentId, d.id),
    agentId,
    ...(clase === 'pdf' ? { portada: urlPortada(agentId, d.id) } : {}),
    ...(d.proporcion ? { proporcion: d.proporcion } : {}),
    ...(d.paginas ? { paginas: d.paginas } : {}),
  };
}

/** Una imagen que ya tiene su dirección (p. ej. la de un informe: «adjunto:<id>» resuelto). */
export function elementoImagen(url: string, nombre: string): ElementoVista {
  return { clave: url, clase: 'imagen', titulo: nombre, detalle: '', nombre, url };
}
